/**
 * Extraction de texte côté client depuis un File (PDF / DOCX / TXT / CSV / HTML).
 *
 * Utilisé par la modale "Compilation multi-doc" pour calculer la taille
 * cumulée des sources et envoyer le texte agrégé au backend.
 *
 * Les libs (pdfjs, mammoth) sont chargées via dynamic import → bundle initial
 * léger, les ~2 Mo de pdfjs ne sont téléchargés qu'à la 1re utilisation.
 *
 * @param {File} file
 * @returns {Promise<{ text: string, pages?: number, source: string }>}
 *          source = 'pdfjs' | 'mammoth' | 'text' selon la méthode utilisée
 */
export async function extractTextFromFile(file) {
    const mime = file.type || '';
    const name = (file.name || '').toLowerCase();

    // PDF → pdfjs-dist (page par page concaténée)
    if (mime === 'application/pdf' || name.endsWith('.pdf')) {
        return extractPdf(file);
    }

    // DOCX → mammoth
    if (
        mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mime === 'application/msword' ||
        name.endsWith('.docx') || name.endsWith('.doc')
    ) {
        return extractDocx(file);
    }

    // Texte brut, CSV, HTML → file.text()
    if (mime.startsWith('text/') || ['txt', 'csv', 'html', 'md'].some(ext => name.endsWith('.' + ext))) {
        const text = await file.text();
        const cleaned = mime === 'text/html' ? stripHtml(text) : text;
        return { text: cleaned, source: 'text' };
    }

    throw new Error(`Format non supporté pour l'extraction texte: ${mime || name}`);
}

async function extractPdf(file) {
    const pdfjs = await import('pdfjs-dist/build/pdf.mjs');
    // Worker via CDN-less inline (dataURL) — évite d'avoir à publier le worker
    // dans /public et de gérer son chemin selon dev/prod.
    pdfjs.GlobalWorkerOptions.workerSrc = await import('pdfjs-dist/build/pdf.worker.mjs?url').then(m => m.default);

    const buffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buffer }).promise;
    const parts = [];
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map(it => it.str).join(' ');
        parts.push(pageText);
    }
    return {
        text: parts.join('\n\n'),
        pages: pdf.numPages,
        source: 'pdfjs',
    };
}

async function extractDocx(file) {
    const mammoth = (await import('mammoth/mammoth.browser.min.js')).default;
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return {
        text: result.value || '',
        source: 'mammoth',
    };
}

function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
}

/**
 * Estimation de tokens : approximation simple chars / 4.
 * Valable pour Claude/GPT en français/anglais.
 */
export function estimateTokens(text) {
    return Math.ceil((text || '').length / 4);
}
